import React, { useCallback, useState, useLayoutEffect } from 'react';
import { UserAccount, Button } from 'lego-on-react';
import block from 'propmods';
import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Access';
import { useCurrentAliceApp } from '../../../hooks/useCurrentAliceApp';
import { shareApi } from '../../../api';
import { useFetchingHandler } from '../../../hooks/useFetchingHandler';
import { Loading } from '../../Loading/Loading';
import MinusIcon from '../../../icons/minus.svg';
import { FormError } from '../../Form/FormError';
import { UserEntity } from '../../../types/utils';

import './UsersPage.scss';

const b = block('UsersPage');
const t = i18n(keyset);

interface UserProps {
    user: UserEntity;
}

const makeEmail = (login: string) => {
    return login.includes('@') ? login : `${login}@yandex.ru`;
};

const User: React.FC<UserProps> = ({ user }) => {
    const skill = useCurrentAliceApp()!;
    const [isDeleted, setIsDeleted] = useState(false);

    const [deleteShareHandler, state] = useFetchingHandler({
        handler: shareApi.deleteUserShare,
        onLoad: () => setIsDeleted(true),
    });

    const deleteShare = useCallback(async () => {
        await deleteShareHandler({ userId: user.uid, skillId: skill.id }, skill.channel);
    }, [skill.id, user.uid]);

    return (
        <div {...b('user')}>
            {isDeleted ? (
                <div {...b('deletion-text')}>{t('users-deleted')}</div>
            ) : (
                <>
                    <div {...b('user-account')}>
                        <UserAccount uid={user.uid} name={makeEmail(user.login)} hasAccentLetter />
                    </div>
                    <div {...b('delete-icon')} onClick={deleteShare} title={t('deny-access')}>
                        {state === 'loading' ? <Loading size="xxs" centered /> : <MinusIcon />}
                    </div>
                </>
            )}
        </div>
    );
};

export const UsersPage: React.FC = () => {
    const skill = useCurrentAliceApp()!;
    const [users, setUsers] = useState<UserEntity[]>([]);

    const [fetchUsersHandler, state] = useFetchingHandler({
        handler: shareApi.getUserShares,
    });

    const fetchUsers = useCallback(async () => {
        const users: UserEntity[] = await fetchUsersHandler({ skillId: skill.id }, skill.channel);

        setUsers(users);
    }, [skill.id]);

    useLayoutEffect(() => {
        void fetchUsers();
    }, [skill.id]);

    return (
        <div {...b()}>
            <p>{t('users-descr')}</p>
            <div {...b('content')}>
                {(() => {
                    switch (state) {
                        case 'idle':
                            return users.length > 0 ? (
                                <div {...b('list')}>
                                    {users.map((user, i) => (
                                        <User user={user} key={i} />
                                    ))}
                                </div>
                            ) : (
                                t('users-only-you')
                            );
                        case 'loading':
                            return <Loading centered size="l" />;
                        case 'error':
                            return (
                                <div>
                                    <div {...b('error')}>
                                        <FormError>{t('error')}</FormError>
                                    </div>
                                    <Button size="s" theme="normal" onClick={fetchUsers}>
                                        {t('users-button-reload')}
                                    </Button>
                                </div>
                            );
                    }
                })()}
            </div>
        </div>
    );
};
